
import { defineComponent } from 'vue';
import Inputmask from 'inputmask';
import InputFieldMixin from '~/components/Inputs/_InputFieldMixin';
import CurrencyFormatLong from '~/filters/CurrencyFormatLong';

export default defineComponent({
  name: 'CurrencyInput',

  mixins: [InputFieldMixin],

  props: {
    value: { type: String, default: '' },
    hint: { type: String, default: '' },
    min: { type: [Number, String], default: null },
    max: { type: [Number, String], default: null },
    showIcon: { type: Boolean, default: false },
    outlined: { type: Boolean, default: false },
    allowCents: { type: Boolean, default: false },
    currencySymbolInMask: { type: Boolean, default: false },
    allowNegative: { type: Boolean, default: false },
  },

  data: () => ({
    defaultLabelAttr: 'Amount',
  }),

  mounted() {
    // aligns the label above the icon even if it's not outlined
    if (this.showIcon && !this.outlined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.$refs.input as any).prependWidth = 24;
    }
  },

  computed: {
    inputMask() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const inputMask: any = {
        showMaskOnHover: false,
        alias: 'numeric',
        groupSeparator: ',',
        digitsOptional: false,
        rightAlign: false,
        autoUnmask: true,
        digits: 2,
        allowMinus: this.allowNegative,
        greedy: true,
        allowPlus: false,
        prefix: this.currencySymbolInMask ? '$' : undefined,
        definitions: {
          0: { validator: '0' }, // disable editing the .00 part of the mask
        },
        radixPoint: '.',
        positionCaretOnClick: 'radixFocus',
        _mask: () => '[-](,999){*|1}', // remove the leading zero
      };

      if (this.allowCents) {
        inputMask.alias = 'decimal';
        inputMask.numericInput = true;
        inputMask.positionCaretOnClick = 'lvp';
        inputMask.digits = Number(this.value) ? 2 : 1;
        // eslint-disable-next-line no-underscore-dangle
        delete inputMask._mask;
        delete inputMask.definitions;
      }

      return inputMask;
    },
  },

  methods: {
    resetInputMask() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const el = (this.$refs.input as any).$el.querySelector('input')!;
      el.inputmask!.remove();
      Inputmask(this.inputMask).mask(el);
      /* eslint-enable */
    },

    onClick() {
      this.$emit('click');
      this.moveCaretFromCentsToRadix();
    },

    onKeyDown(event: KeyboardEvent) {
      const disallowedKeys = ['+'];
      if (!this.allowNegative) {
        disallowedKeys.push('-');
      }

      if (disallowedKeys.includes(event.key)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }

      if (this.allowCents && !this.value && event.key === '0') {
        // In this case a user entered the 0 key as the first digit in the currency input which
        // would trigger the underlying text-field to set its internal value to `isDirty: true`.
        // And because of the way that `allowCents` input works we would not have a way to reset
        // that state therefor we skip setting it in the first place with this.
        event.preventDefault();
      }
    },

    moveCaretFromCentsToRadix() {
      if (this.allowCents) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const el = (this.$refs.input as any).$el.querySelector('input');
      const start = el.selectionStart;
      if (!Number.isInteger(start)) return;

      const end = el.selectionEnd;
      if (start !== end) return; // multiple characters selected

      const [, formatted] = CurrencyFormatLong(el.value).split('$');
      const dotPosition = formatted.length - 3;
      if (start && start < dotPosition) return; // not clicked in decimal area

      window.setTimeout(() => el.setSelectionRange(dotPosition, dotPosition), 0);
      /* eslint-enable */
    },

    currencyValidation(value: string | number | undefined): string | boolean {
      if (!value) return this.isRequired();

      const val = Number(value);
      if (Number.isNaN(val)) return 'Not a number';

      if (this.min !== null && val < Number(this.min)) return `Value needs to be more than ${this.min}`;
      if (this.max !== null && val > Number(this.max)) return `Value needs to be less than ${this.max}`;

      return true;
    },

    updateAndResetMask(value: string | number) {
      this.$emit('input', value);
      this.resetInputMask();
    },
  },

  watch: {
    value(newVal?: string, oldVal?: string) {
      if (this.allowCents) {
        const num = Number(newVal);

        if (!newVal || num === 0 || newVal === '.00') {
          this.updateAndResetMask('');
        } else if (!oldVal) {
          // Obtaining decimal point
          // Also fixing Javascript float shenanigans
          const val = (num * 10) / 100;
          this.updateAndResetMask(val.toString());
        }
      }
      if (newVal === '.00') {
        this.updateAndResetMask('');
      }
    },

    allowNegative() {
      this.resetInputMask();
    },
  },
});
